import type { EnvironmentThreadShell } from "@cleo/client-runtime/state/models";
import {
  type EnvironmentId,
  type ModelSelection,
  type ProjectId,
  type RuntimeMode,
  type ScopedSettingsSource,
  resolveScopedSetting,
} from "@cleo/contracts";

import { type RootSessionLaunchInput } from "../rootSessionBirth";
import { DEFAULT_INTERACTION_MODE, DEFAULT_RUNTIME_MODE } from "../types";

// The base case of the world view (principal correction, 2026-08-24): a bare
// authoritative Session — S_harmony_session born with its World, or a root Session just
// persisted — must be able to launch its FIRST execution. Nothing about that
// launch may depend on another execution: configuration comes from the
// project the person picks (its default model, resolved against the
// environment's live providers) and the harness defaults. A parent execution
// is lineage only, carried when it exists in the same environment, never
// required.

export interface WorldSessionLaunchProject {
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  readonly title: string;
  /** The folder this project runs in — a project IS a folder pointer, code or not. */
  readonly workspaceRoot: string;
  /** The project's default model resolved against live providers; null = nothing to launch on. */
  readonly modelSelection: ModelSelection | null;
}

export type WorldSessionFirstExecutionRefusal = {
  readonly _tag: "Refused";
  readonly reason:
    | "invalid-coordinate"
    | "empty-ask"
    | "project-required"
    | "files-root-missing"
    | "provider-required"
    | "parent-coordinate-mismatch";
};

export interface PreparedWorldSessionFirstExecution {
  readonly _tag: "Launch";
  readonly environmentId: EnvironmentId;
  /** Feeds `createRootSessionLauncher`, whose `prepareRootSessionLaunch` mints the exact wire input. */
  readonly input: RootSessionLaunchInput;
}

/**
 * UP7: the permission mode a NEW World session starts in — the caller's
 * explicit choice, else the room override, else the World override, else
 * this Mac's default. Callers that carry the settings pass them; a caller
 * with no settings source keeps the contract's default. Never the tool
 * boundary.
 */
export function resolveWorldSessionRuntimeMode(input: {
  readonly worldKey: string;
  readonly roomId?: string | null | undefined;
  readonly runtimeMode?: RuntimeMode | undefined;
  readonly settings?: ScopedSettingsSource | undefined;
}): RuntimeMode {
  if (input.runtimeMode !== undefined) return input.runtimeMode;
  if (input.settings === undefined) return DEFAULT_RUNTIME_MODE;
  return resolveScopedSetting(input.settings, "defaultRuntimeMode", {
    worldKey: input.worldKey,
    roomId: input.roomId ?? null,
  }).value;
}

/**
 * UP7: the permission mode an ungoverned NEW draft starts in — the carried
 * or explicit choice, else the active World's override, else this Mac's
 * default. Draft births run where no room is seated, so the room layer only
 * applies when the caller names one; a governed birth carries its room
 * through `prepareWorldRootSessionExecutionBirth`. Local drafts (no World)
 * read this Mac's plain default. Never the tool boundary.
 */
export function resolveNewDraftRuntimeMode(input: {
  readonly worldKey: string | null | undefined;
  readonly roomId?: string | null | undefined;
  readonly explicit?: RuntimeMode | null | undefined;
  readonly settings: ScopedSettingsSource;
}): RuntimeMode {
  if (input.explicit != null) return input.explicit;
  const worldKey = input.worldKey?.trim() ?? "";
  if (worldKey === "") return input.settings.defaultRuntimeMode;
  return resolveWorldSessionRuntimeMode({
    worldKey,
    roomId: input.roomId ?? null,
    settings: input.settings,
  });
}

export function prepareWorldSessionFirstExecution(input: {
  readonly worldKey: string;
  readonly sessionId: string;
  /** UP7: the room this session is born in, when the caller knows it. */
  readonly roomId?: string | null | undefined;
  /** UP7: an explicit permission mode wins over every override. */
  readonly runtimeMode?: RuntimeMode | undefined;
  /** UP7: the settings the override record lives in; absent keeps the default. */
  readonly settings?: ScopedSettingsSource | undefined;
  readonly workroomKey?: string | undefined;
  readonly ask: string;
  readonly project: WorldSessionLaunchProject | null;
  /** The World's device-local non-code files root on the launch environment (O11/O13). */
  readonly filesRoot: string | null;
  readonly parentExecution: EnvironmentThreadShell | null;
  readonly now: string;
}): PreparedWorldSessionFirstExecution | WorldSessionFirstExecutionRefusal {
  if (!input.worldKey.trim() || !input.sessionId.trim()) {
    return { _tag: "Refused", reason: "invalid-coordinate" };
  }
  if (!input.ask.trim()) return { _tag: "Refused", reason: "empty-ask" };
  const project = input.project;
  if (project === null) return { _tag: "Refused", reason: "project-required" };
  // World folders are optional source references, never an execution prerequisite.
  if (project.modelSelection === null) return { _tag: "Refused", reason: "provider-required" };
  const parent = input.parentExecution;
  if (parent !== null && (parent.worldKey !== input.worldKey || parent.archivedAt !== null)) {
    return { _tag: "Refused", reason: "parent-coordinate-mismatch" };
  }
  // Lineage rides only inside one environment: the session tree is projected
  // per environment, so a cross-environment parent would be a dangling link.
  const parentThreadId =
    parent !== null && parent.environmentId === project.environmentId ? parent.id : null;
  return {
    _tag: "Launch",
    environmentId: project.environmentId,
    input: {
      mode: "world",
      activeWorldKey: input.worldKey,
      activeSessionId: input.sessionId,
      ...(input.workroomKey === undefined ? {} : { workroomKey: input.workroomKey }),
      projectId: project.projectId,
      ask: input.ask,
      now: input.now,
      modelSelection: project.modelSelection,
      runtimeMode: resolveWorldSessionRuntimeMode(input),
      interactionMode: DEFAULT_INTERACTION_MODE,
      branch: null,
      worktreePath: null,
      ...(parentThreadId === null ? {} : { parentThreadId }),
    },
  };
}

export function describeWorldSessionFirstExecutionRefusal(
  reason: WorldSessionFirstExecutionRefusal["reason"],
): string {
  switch (reason) {
    case "invalid-coordinate":
      return "The World or Session address is invalid.";
    case "empty-ask":
      return "Say what this Session's agent should start on.";
    case "files-root-missing":
      return "This World has no files root on this machine yet. Name the folder that holds this World's non-code files to launch.";
    case "project-required":
      return "Choose a project to run this Session's agent in.";
    case "provider-required":
      return "This project has no provider to launch on. Configure one under Settings → Subscription accounts.";
    case "parent-coordinate-mismatch":
      return "The parent Session's execution does not belong to this World.";
  }
}
